import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  Badge,
  Avatar,
  useTheme,
  alpha,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Science,
  Schedule,
  PriorityHigh,
  Biotech,
  CheckCircle,
  Psychology,
  Warning,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import samplesService, { Sample, Priority, SampleStatus } from '../../services/samplesService';

const VolumetriePreparationChimiqueDashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  
  // State management
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preparationDialog, setPreparationDialog] = useState(false);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [preparationNotes, setPreparationNotes] = useState('');
  const [dissolutionMethod, setDissolutionMethod] = useState('acid_dissolution');
  const [stats, setStats] = useState({
    pending_prep: 0,
    in_progress: 0,
    urgent_samples: 0,
    daily_capacity: 20,
  });

  useEffect(() => {
    loadVolumetriePreparationData();
  }, []);

  const loadVolumetriePreparationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load samples specifically in Volumétrie chemical preparation status
      const response = await samplesService.getSamples(1, 100, {
        status: [SampleStatus.LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE],
      });
      
      const volumetriePreparationSamples = response.samples.filter(sample => 
        sample.statut === SampleStatus.LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE
      );
      
      setSamples(volumetriePreparationSamples);
      
      // Calculate stats
      const urgentSamples = volumetriePreparationSamples.filter(
        sample => sample.priorite === Priority.URGENTE
      ).length;
      
      setStats({
        pending_prep: volumetriePreparationSamples.length,
        in_progress: volumetriePreparationSamples.length,
        urgent_samples: urgentSamples,
        daily_capacity: 20,
      });
      
    } catch (error) {
      console.error('Error loading Volumétrie preparation data:', error);
      setError('Erreur lors du chargement des données de préparation chimique Volumétrie');
    } finally {
      setLoading(false);
    }
  };

  const handleStartPreparation = (sample: Sample) => {
    setSelectedSample(sample);
    setPreparationNotes('');
    setDissolutionMethod('acid_dissolution');
    setPreparationDialog(true);
  };

  const handleCompletePreparation = async () => {
    if (!selectedSample) return;
    
    try {
      // Update sample with preparation details
      await samplesService.updateSample(selectedSample.id, {
        metadonnees: {
          ...selectedSample.metadonnees,
          volumetrie_preparation_method: dissolutionMethod,
          volumetrie_preparation_notes: preparationNotes,
          volumetrie_preparation_completed_at: new Date().toISOString(),
          volumetrie_prep_technician_id: user?.id,
        }
      });
      
      // Move sample to Volumétrie analysis status
      await samplesService.updateSampleStatus(
        selectedSample.id,
        SampleStatus.LABORATOIRE_VOLUMETRIE_ANALYSE,
        'Préparation chimique Volumétrie terminée - Prêt pour titrage'
      );
      
      setPreparationDialog(false);
      setSelectedSample(null);
      loadVolumetriePreparationData();
    } catch (error) {
      console.error('Error completing preparation:', error);
      setError('Erreur lors de la finalisation de la préparation');
    }
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.URGENTE:
        return 'error';
      case Priority.ELEVEE:
        return 'warning';
      case Priority.NORMALE:
        return 'default';
      default:
        return 'default';
    }
  };

  const dissolutionMethods = [
    { value: 'acid_dissolution', label: 'Dissolution Acide (HCl + HNO3)' },
    { value: 'alkaline_fusion', label: 'Fusion Alcaline (Na2CO3)' },
    { value: 'aqua_regia', label: 'Eau Régale (HCl + HNO3 3:1)' },
    { value: 'direct_extraction', label: 'Extraction Directe (pour Cu soluble)' },
    { value: 'microwave_digestion', label: 'Digestion Micro-ondes' },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            📊 Préparation Chimique Volumétrie
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Titrage Volumétrique & Analyse Classique - {user?.first_name} {user?.last_name}
          </Typography>
        </Box>
        <Chip
          icon={<Psychology />}
          label={`${samples.length} échantillons en préparation`}
          color="success"
          variant="outlined"
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.success.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.success.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                    En Préparation
                  </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.pending_prep}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.success.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  🧪
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.error.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.error.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.error.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                    Urgents
                  </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.urgent_samples}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.error.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  🚨
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.info.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.info.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.info.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                    Capacité Journalière
                  </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {stats.daily_capacity}
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.info.main}, ${theme.palette.info.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.info.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  ⏰
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
            borderLeft: `6px solid ${theme.palette.primary.main}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 16px 30px ${alpha(theme.palette.primary.main, 0.3)}`,
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
              borderRadius: '50%',
              transform: 'translate(50%, -50%)',
            }
          }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                    Taux Charge
                  </Typography>
                  <Typography variant="h4" sx={{
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontWeight: 'bold'
                  }}>
                    {Math.round((stats.pending_prep / stats.daily_capacity) * 100)}%
                  </Typography>
                </Box>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}>
                  📊
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Samples List */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Échantillons en Préparation Chimique Volumétrie
          </Typography>
          
          {samples.length === 0 ? (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center" 
              minHeight="200px"
              sx={{ 
                color: 'text.secondary',
                backgroundColor: alpha(theme.palette.success.main, 0.02),
                borderRadius: 2,
                border: `1px dashed ${alpha(theme.palette.success.main, 0.2)}`
              }}
            >
              <Psychology sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                Aucun échantillon en préparation
              </Typography>
              <Typography variant="body2" textAlign="center">
                Les échantillons apparaîtront ici lorsqu'ils seront routés vers le laboratoire Volumétrie pour préparation chimique.
              </Typography>
            </Box>
          ) : (
            <List>
              {samples.map((sample, index) => (
                <React.Fragment key={sample.id}>
                  <ListItem 
                    sx={{ 
                      border: '1px solid',
                      borderColor: alpha(theme.palette.success.main, 0.2),
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: alpha(theme.palette.success.main, 0.05)
                    }}
                  >
                    <ListItemIcon>
                      <Badge 
                        badgeContent={sample.priorite === Priority.URGENTE ? "!" : null}
                        color="error"
                      >
                        <Psychology color="success" />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {sample.sample_id}
                          </Typography>
                          <Chip
                            label={sample.priorite}
                            size="small"
                            color={getPriorityColor(sample.priorite)}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Client: {sample.client_nom} | Projet: {sample.project_name || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Méthodes Volumétrie: {sample.analyses_demandees?.filter(a => a.includes('Cu') || a.includes('Titration')).join(', ') || 'Titrage Standard'}
                          </Typography>
                        </Box>
                      }
                    />
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Psychology />}
                      onClick={() => handleStartPreparation(sample)}
                      sx={{ ml: 2 }}
                    >
                      Commencer Préparation
                    </Button>
                  </ListItem>
                  {index < samples.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Preparation Dialog */}
      <Dialog open={preparationDialog} onClose={() => setPreparationDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Psychology color="success" />
            <Typography variant="h6">
              Préparation Chimique Volumétrie - {selectedSample?.sample_id}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Méthode de Dissolution/Préparation
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Méthode de préparation</InputLabel>
              <Select
                value={dissolutionMethod}
                onChange={(e) => setDissolutionMethod(e.target.value)}
              >
                {dissolutionMethods.map(method => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes de préparation"
              value={preparationNotes}
              onChange={(e) => setPreparationNotes(e.target.value)}
              placeholder="Observations, conditions particulières, problèmes rencontrés..."
              sx={{ mb: 2 }}
            />

            {dissolutionMethod === 'alkaline_fusion' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  ⚠️ Attention: Fusion alcaline à haute température - Précautions requises
                </Typography>
              </Alert>
            )}

            {dissolutionMethod === 'direct_extraction' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  ℹ️ Extraction directe - Méthode rapide pour métaux solubles
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreparationDialog(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleCompletePreparation}
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
          >
            Finaliser Préparation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VolumetriePreparationChimiqueDashboard; 